import React from 'react';
import type { User, Lead } from '../../store/types';

export interface NavItem {
  label: string;
  path: string;
  icon: string;
}

// Count of leads whose follow-up is due (today or overdue) and still open —
// drives the dot on the Leads nav item.
export const leadsDueCount = (leads: Lead[]): number => {
  const t = new Date();
  const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  return leads.filter((l) => l.followUpAt && l.followUpAt <= today && l.status !== 'won' && l.status !== 'lost').length;
};

export const getNavItems = (role: User['role'] | undefined): NavItem[] => {
  const isCoach = role === 'coach' || role === 'owner';
  // Coach nav is the working set only — Messages/Feed are demo-era surfaces that
  // sit empty on live accounts (routes still work by URL; just not advertised).
  return isCoach
    ? [
        { label: 'Today', path: '/coach/overview', icon: 'users' },
        { label: 'Planner', path: '/coach', icon: 'calendar' },
        { label: 'Calendar', path: '/coach/calendar', icon: 'clock' },
        { label: 'Leads', path: '/coach/leads', icon: 'leads' },
        { label: 'Library', path: '/coach/library', icon: 'book' },
      ]
    : [
        { label: 'Today', path: '/client', icon: 'home' },
        { label: 'Vault', path: '/client/vault', icon: 'image' },
        { label: 'Health', path: '/client/health', icon: 'chart' },
        { label: 'Feed', path: '/community', icon: 'globe' },
        { label: 'Fuel', path: '/nutrition', icon: 'flame' },
        { label: 'Messages', path: '/messages', icon: 'chat' },
        { label: 'Profile', path: '/profile', icon: 'user' },
      ];
};

export const NavIcon: React.FC<{ name: string; className?: string }> = ({ name, className = 'w-5 h-5' }) => {
  const s = 'stroke-current stroke-[1.5] fill-none';
  switch (name) {
    case 'users':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle className={s} cx="9" cy="7" r="4" />
          <path className={s} d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path className={s} d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'book':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path className={s} d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'calendar':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <rect className={s} x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <path className={s} d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      );
    case 'globe':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle className={s} cx="12" cy="12" r="10" />
          <path className={s} d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      );
    case 'chat':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'home':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline className={s} points="9 22 9 12 15 12 15 22" />
        </svg>
      );
    case 'image':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <rect className={s} x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle className={s} cx="8.5" cy="8.5" r="1.5" />
          <polyline className={s} points="21 15 16 10 5 21" />
        </svg>
      );
    case 'chart':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M3 3v18h18" />
          <path className={s} d="M18 17V9M13 17V5M8 17v-3" />
        </svg>
      );
    case 'flame':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.5 1.5-2.5 3-2.5 5a2.5 2.5 0 0 0 2.5 2.5z" />
          <path className={s} d="M12 21a9 9 0 0 0 9-9c0-1.5-1-2-2-3.5-1 1.5-2 2.5-3 3.5" />
        </svg>
      );
    case 'user':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle className={s} cx="12" cy="7" r="4" />
        </svg>
      );
    case 'leads':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path className={s} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle className={s} cx="9" cy="7" r="4" />
          <path className={s} d="M19 8v6M22 11h-6" />
        </svg>
      );
    case 'clock':
      return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle className={s} cx="12" cy="12" r="9" />
          <path className={s} d="M12 7v5l3 2" />
        </svg>
      );
    default:
      return <span className="text-lg leading-none">•</span>;
  }
};
